import { Component, OnInit } from '@angular/core';
import { ContractsService } from '../contracts.service';
import { Contracts } from '../models/contracts.model';
import { MenuItem, MessageService } from 'primeng/api';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ContractRevisions } from '../models/contract-revisions.model';
import { formatDate, CommonModule } from '@angular/common';
import { ShipmentDeliveryAttachment } from "../../shipment/models/shipment-delivery.attachment";
import { SessionStorageService } from "../../services/session-storage.service";
import { VehicleConfig } from "../../fleet/vehicle/params/vehicle-config";
import { ContractStatus } from "../models/contract-status.model";

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { TabsModule } from 'primeng/tabs';
import { StepsModule } from 'primeng/steps';
import { DividerModule } from 'primeng/divider';
import { ChipModule } from 'primeng/chip';
import { ImageModule } from 'primeng/image';
import { TimelineModule } from 'primeng/timeline';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressBarModule } from "primeng/progressbar";
import { AvatarModule } from "primeng/avatar";
import { RadioButtonModule } from "primeng/radiobutton";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { TextareaModule } from 'primeng/textarea';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { ContractCrmContext } from '../models/contract-crm-context.model';
import { QuotationContractLink } from '../../crm/quotations/quotation-contract-conversion.model';
import { ToolbarService } from '../../services/toolbar.service';
import { ContractLifecycleState, ContractObligation, ContractRenewal, ContractTransitionRequest, ContractValidationBlocker } from '../models/contract-lifecycle.model';
import { LogisticsProjectsService } from '../../logistics-projects/logistics-projects.service';


@Component({
    selector: 'app-view-contract',
    templateUrl: './view-contract.component.html',
    styleUrls: ['./view-contract.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        TableModule,
        ToolbarModule,
        BreadcrumbModule,
        InputTextModule,
        CardModule,
        TagModule,
        TabsModule,
        StepsModule,
        DividerModule,
        ChipModule,
        ImageModule,
        TimelineModule,
        DialogModule,
        SelectModule,
        CheckboxModule,
        ProgressBarModule,
        AvatarModule,
        RadioButtonModule,
        ConfirmDialogModule,
        TextareaModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [
        ContractsService,
        LogisticsProjectsService,
        MessageService,
        SessionStorageService
    ]
})
export class ViewContractComponent implements OnInit {
    private readonly tabKeys = ['revision-details', 'delivery-rate-cards', 'contract-files', 'generated-documents', 'approvals', 'renewals', 'obligations', 'audit'] as const;

    contract!: Contracts;
    crmContext?: ContractCrmContext;
    sourceQuotations: QuotationContractLink[] = [];
    lifecycle?: ContractLifecycleState;
    lifecycleBlockers: ContractValidationBlocker[] = [];
    isLoading = false;
    lifecycleActionInProgress?: string;
    lifecycleNotice = '';

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    pickupSubLocality: string = '';
    pickupLocality: string = '';
    dropoffSubLocality: string = '';
    dropoffLocality: string = '';

    contractFilesList: ShipmentDeliveryAttachment[] = [];
    contractFilesCount = 0;
    currentRevisionNumber = 0;

    transitionReason = '';
    activationOptions = {
        signatureOverride: false,
        approvalOverride: false,
        documentWaiver: false,
        rateWaiver: false
    };

    revisionFields: { key: keyof ContractRevisions, label: string, type: 'string' | 'date' | 'boolean' }[] = [
        { key: 'effectiveStartDate', label: 'Effective Start Date', type: 'date' },
        { key: 'endDate', label: 'End Date', type: 'date' },
        { key: 'firstPartyAgreed', label: 'Seller (First Party) Agreed', type: 'boolean' }, // Assuming boolean displayed as Yes/No
        { key: 'secondPartyAgreed', label: 'Buyer (Second Party) Agreed', type: 'boolean' }, // Assuming boolean displayed as Yes/No
        { key: 'isRenewable', label: 'Is Renewable', type: 'boolean' }, // Assuming boolean displayed as Yes/No
        { key: 'agreementPeriod', label: 'Agreement Period', type: 'string' },
        { key: 'nonRenewalDisclosureBeforeDays', label: 'Non-Renewal Disclosure (Days)', type: 'string' }, // Number displayed as string
        { key: 'goodsInsuranceByFirstParty', label: 'Goods Insured by Seller (First Party)', type: 'boolean' }, // Assuming boolean displayed as Yes/No
        { key: 'goodsInsuranceBySecondParty', label: 'Goods Insured by Buyer (Second Party)', type: 'boolean' }, // Assuming boolean displayed as Yes/No
        { key: 'totalAmount', label: 'Total Amount', type: 'string' }, // Number/currency displayed as string
        { key: 'vatAddedToInvoice', label: 'VAT Added to Invoice', type: 'boolean' }, // Assuming boolean displayed as Yes/No
        { key: 'paymentTerms', label: 'Payment Terms', type: 'string' },
        { key: 'penaltyRate', label: 'Penalty Rate', type: 'string' }, // Number/percentage displayed as string
        { key: 'firstPartyBankAccountId', label: 'Seller (First Party) Bank Account ID', type: 'string' },
        { key: 'firstPartyBankAccountDetails', label: 'Seller (First Party) Bank Account Details', type: 'string' },
        { key: 'siteServiceTime', label: 'Site Service Time', type: 'string' },
        { key: 'penaltyOnServiceTime', label: 'Penalty on Service Time', type: 'string' }, // Assuming boolean displayed as Yes/No
        { key: 'noticePeriod', label: 'Notice Period', type: 'string' },
        { key: 'forceMajeureClausePeriod', label: 'Force Majeure Clause Period', type: 'string' },
        { key: 'disputeResolution', label: 'Dispute Resolution', type: 'string' },
        { key: 'governingLaw', label: 'Governing Law', type: 'string' },
        { key: 'createdBy', label: 'Created By', type: 'string' },
        { key: 'createdDate', label: 'Created Date', type: 'date' },
        { key: 'revisedBy', label: 'Revised By', type: 'string' },
        { key: 'revisedDate', label: 'Revised Date', type: 'date' },
        { key: 'revisionNotes', label: 'Revision Notes', type: 'string' }
        // deliveryRateCards is already handled in a separate table
    ];

    constructor(
        private contractsService: ContractsService,
        private messageService: MessageService,
        private route: ActivatedRoute, private router: Router,
        private sessionStorageService: SessionStorageService,
        private toolbarService: ToolbarService,
        private logisticsProjectsService: LogisticsProjectsService
    ) { }

    ngOnInit(): void {
        this.isLoading = true;
        this.breadcrumbItems = [
            { label: 'Contracts', routerLink: '/contracts' },
            { label: 'View Contract' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        const contractId = this.route.snapshot.paramMap.get('id');
        this.route.queryParamMap.subscribe(queryParams => {
            const revisionValue = Number(queryParams.get('revision'));
            if (!Number.isNaN(revisionValue) && revisionValue >= 0) {
                this.currentRevisionNumber = revisionValue;
            }
            this.activeTabIndex = this.resolveTabIndex(queryParams.get('tab'));
        });
        if (contractId) {
            this.loadContract(Number(contractId));
        }
    }

    loadContract(contractId: number) {
        this.contractsService.getContractById(contractId).subscribe(contract => {
            console.log(contract);
            this.contract = contract;
            this.contract.revisions = (this.contract.revisions ?? []).map(revision => this.normalizeRevision(revision));
            this.contract.contractStatus = this.contract.revisions
                .sort((a, b) => a.revisionNumber - b.revisionNumber)
                .map(value => value.status).pop() || ''
            this.isLoading = false;
            if (this.contract.revisions.length > 0) {
                if (this.currentRevisionNumber >= this.contract.revisions.length) {
                    this.currentRevisionNumber = 0;
                }
                this.loadContractFiles(this.contract.revisions[this.currentRevisionNumber]);
                this.loadDeliveryRateCards(this.contract.revisions[this.currentRevisionNumber]);
                this.loadLifecycle(this.contract.revisions[this.currentRevisionNumber]);
            }
            this.loadCrmContext(contract.contractID);
            this.loadSourceQuotations(contract.contractID);
        });
    }

    private loadCrmContext(contractId: number) {
        this.contractsService.getCrmContextForContract(contractId).subscribe(context => {
            this.crmContext = context;
        });
    }

    private loadSourceQuotations(contractId: number) {
        this.contractsService.getSourceQuotations(contractId).subscribe(links => {
            this.sourceQuotations = links || [];
        });
    }


    onRevisionTabChange(event: any) {
        const revisionIndex = typeof event === 'number' ? event : event?.index ?? 0;
        this.currentRevisionNumber = revisionIndex;
        this.contractFilesList = [];
        this.loadContractFiles(this.contract.revisions[revisionIndex]);
        this.loadDeliveryRateCards(this.contract.revisions[revisionIndex]);
        this.loadLifecycle(this.contract.revisions[revisionIndex]);
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { revision: revisionIndex },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    activeTabIndex: number = 0; // Added activeTabIndex for the outer tabs
    onTabChange(event: any) { // Added onTabChange for the inner tabs
        this.activeTabIndex = typeof event === 'number' ? event : event?.index ?? 0;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.tabKeys[this.activeTabIndex] || this.tabKeys[0] },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private resolveTabIndex(tabKey: string | null): number {
        if (!tabKey) {
            return 0;
        }
        const tabIndex = this.tabKeys.findIndex(key => key === tabKey.toLowerCase());
        return tabIndex >= 0 ? tabIndex : 0;
    }

    addRateCard() {
        this.router.navigate([
            '/contracts/create-rate-card/contract',
            this.contract.contractID,
            'revision',
            this.contract.revisions[this.currentRevisionNumber].revisionID,
            'rate'
        ]);
    }

    openRateCardsWorkspace(contractRevision: ContractRevisions = this.contract.revisions[this.currentRevisionNumber]) {
        this.router.navigate(['/shipment/rate-cards'], {
            queryParams: {
                contractId: this.contract.contractID,
                revisionId: contractRevision.revisionID
            }
        });
    }

    private normalizeRevision(contractRevision: ContractRevisions): ContractRevisions {
        return {
            ...contractRevision,
            deliveryRateCards: Array.isArray(contractRevision?.deliveryRateCards) ? contractRevision.deliveryRateCards : []
        } as ContractRevisions;
    }

    getRateCardCount(contractRevision?: ContractRevisions): number {
        return Array.isArray(contractRevision?.deliveryRateCards) ? contractRevision.deliveryRateCards.length : 0;
    }

    private toDate(value: Date | string | number | undefined | null): Date | undefined {
        if (!value) {
            return undefined;
        }
        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }

    private getRevisionCreatedTimestamp(contractRevision: ContractRevisions): number {
        return this.toDate(contractRevision?.createdDate as Date | string | number | undefined)?.getTime() ?? new Date().getTime();
    }

    loadContractFiles(contractRevision: ContractRevisions) {
        // Replace with your actual logic to fetch contract files
        if (contractRevision.originalAgreementDocumentUrl)
            this.contractFilesList.push({
                fileName: 'Original Agreement',
                fileType: 'Contract',
                fileUrl: contractRevision.originalAgreementDocumentUrl,
                creationDate: this.getRevisionCreatedTimestamp(contractRevision),
            } as ShipmentDeliveryAttachment);
        if (contractRevision.signedAgreementDocumentUrl)
            this.contractFilesList.push({
                fileName: 'Signed Agreement',
                fileType: 'Contract',
                fileUrl: contractRevision.signedAgreementDocumentUrl,
                creationDate: this.getRevisionCreatedTimestamp(contractRevision),
            } as ShipmentDeliveryAttachment);
        this.contractFilesCount = this.contractFilesList.length;
    }

    showDate(date: Date | undefined): string {
        if (date) {
            return formatDate(date, 'dd/MM/yyyy', 'en-US');
        }
        return '';
    }

    private loadDeliveryRateCards(contractRevision: ContractRevisions) {
        this.contractsService.getDeliveryRateCardsByRevisionId(this.contract.contractID, contractRevision.revisionID).subscribe(deliveryRateCards => {
            console.log(deliveryRateCards);
            contractRevision.deliveryRateCards = deliveryRateCards;
            this.loadLifecycle(contractRevision);
        });
    }

    private loadLifecycle(contractRevision: ContractRevisions) {
        if (!this.contract?.contractID || !contractRevision?.revisionID) {
            return;
        }
        this.contractsService.getRevisionLifecycle(this.contract.contractID, contractRevision.revisionID, this.sessionStorageService.getCompany()?.id).subscribe(lifecycle => {
            this.lifecycle = lifecycle;
            this.lifecycleBlockers = lifecycle.activationBlockers || [];
        });
    }

    getVehicleConfig(vehicleConfigId: number): VehicleConfig | undefined {
        return this.sessionStorageService.getVehicleTypes()?.flatMap(value => value.vehicleConfigs).filter(value => value.configId == vehicleConfigId).pop();
    }

    getCurrencyCode(): string {
        return this.sessionStorageService.getCountryList()?.find(value => value.id == this.contract.firstParty.countryId)?.currencyCode ?? '';
    }

    getContractMetrics(): PageMetric[] {
        const revisions = this.contract?.revisions ?? [];
        const currentRevision = revisions[this.currentRevisionNumber];

        return [
            { label: 'Revisions', value: revisions.length, hint: 'Stored versions', tone: 'info' },
            { label: 'Current Status', value: this.contract?.contractStatus || 'Pending', hint: 'Latest revision', tone: this.contract?.contractStatus === ContractStatus.ACTIVE ? 'success' : 'warn' },
            { label: 'Total Amount', value: currentRevision?.totalAmount ?? '-', hint: this.getCurrencyCode(), tone: 'info' },
            { label: 'Rate Cards', value: currentRevision?.deliveryRateCards?.length ?? 0, hint: 'Current revision', tone: 'success' }
        ];
    }

    showAsDate(contractElement: any) {
        return this.toDate(contractElement)?.toLocaleDateString() ?? '';
    }

    addRevision() {
        this.router.navigate(['/contracts/create-revision/contract', this.contract.contractID, 'revision']);
    }

    editRevision() {
        this.router.navigate(['/contracts/edit-revision/contract', this.contract.contractID, 'revision', this.contract.revisions[this.currentRevisionNumber].revisionID]);
    }

    openDocumentWorkspace() {
        this.router.navigate([
            '/contracts/view',
            this.contract.contractID,
            'revision',
            this.contract.revisions[this.currentRevisionNumber].revisionID,
            'document-workspace'
        ]);
    }

    editContract() {
        this.router.navigate(['/contracts/edit', this.contract.contractID]);
    }

    createLogisticsProjectFromContract(): void {
        const companyId = this.sessionStorageService.getCompany()?.id || this.toolbarService.customer()?.company?.id;
        if (!this.contract?.contractID || !companyId) {
            return;
        }
        this.logisticsProjectsService.createProjectFromContract(this.contract.contractID, companyId).subscribe({
            next: project => {
                this.messageService.add({ severity: 'success', summary: 'Project created', detail: project.projectNumber || 'Logistics project created.' });
                if (project.projectId) {
                    this.router.navigate(['/logistics-projects', project.projectId]);
                }
            },
            error: error => this.messageService.add({
                severity: 'error',
                summary: 'Project failed',
                detail: error?.error?.message || 'Unable to create logistics project from contract.'
            })
        });
    }

    getHealthFlags(): string[] {
        return this.crmContext?.healthFlags ?? [];
    }

    lifecycleActions(): { action: string; label: string; icon: string; severity?: 'secondary' | 'success' | 'danger' | 'help' | 'info' | 'warn' }[] {
        const actions = this.lifecycle?.validActions || [];
        const meta: Record<string, { label: string; icon: string; severity?: 'secondary' | 'success' | 'danger' | 'help' | 'info' | 'warn' }> = {
            SUBMIT: { label: 'Submit', icon: 'fa-solid fa-paper-plane', severity: 'info' },
            APPROVE: { label: 'Approve', icon: 'fa-solid fa-check', severity: 'success' },
            REJECT: { label: 'Reject', icon: 'fa-solid fa-xmark', severity: 'danger' },
            MARK_PENDING_SIGNATURE: { label: 'Pending Signature', icon: 'fa-solid fa-signature', severity: 'help' },
            ACTIVATE: { label: 'Activate', icon: 'fa-solid fa-circle-check', severity: 'success' },
            TERMINATE: { label: 'Terminate', icon: 'fa-solid fa-ban', severity: 'danger' },
            EXPIRE: { label: 'Expire', icon: 'fa-solid fa-calendar-xmark', severity: 'warn' },
            CREATE_AMENDMENT: { label: 'Create Amendment', icon: 'fa-solid fa-code-branch', severity: 'secondary' },
            CREATE_RENEWAL: { label: 'Create Renewal', icon: 'fa-solid fa-rotate', severity: 'secondary' }
        };
        return actions.map(action => ({ action, ...(meta[action] || { label: action, icon: 'fa-solid fa-play', severity: 'secondary' }) }));
    }

    runLifecycleAction(action: string): void {
        const contractId = this.contract?.contractID;
        const revision = this.contract?.revisions?.[this.currentRevisionNumber];
        if (this.lifecycleActionInProgress) return;
        if (!contractId || !revision?.revisionID) {
            this.messageService.add({ severity: 'error', summary: 'Lifecycle action unavailable', detail: 'The selected contract revision could not be identified.' });
            return;
        }
        this.lifecycleActionInProgress = action;
        this.lifecycleNotice = `${this.lifecycleActionLabel(action)} in progress…`;
        const currentCustomer = this.toolbarService.customer() ?? this.sessionStorageService.getCustomer();
        const request: ContractTransitionRequest = {
            action,
            actorId: currentCustomer?.id,
            companyId: this.sessionStorageService.getCompany()?.id ?? currentCustomer?.company?.id,
            reason: this.transitionReason,
            comments: this.transitionReason,
            ...this.activationOptions
        };
        this.contractsService.transitionRevision(contractId, revision.revisionID, request).pipe(
            finalize(() => this.lifecycleActionInProgress = undefined)
        ).subscribe({
            next: result => {
                this.lifecycle = result.lifecycle;
                this.lifecycleBlockers = result.blockers?.length ? result.blockers : result.lifecycle?.activationBlockers || [];
                if (result.transitioned && (!result.blockers || result.blockers.length === 0)) {
                    this.lifecycleNotice = `Revision is now ${result.newStatus}.`;
                    this.messageService.add({ severity: 'success', summary: 'Lifecycle updated', detail: `${action} completed.` });
                    this.loadContract(contractId);
                } else {
                    this.lifecycleNotice = 'Activation is blocked until the listed requirements are met or waived.';
                    this.messageService.add({ severity: 'warn', summary: 'Action blocked', detail: this.lifecycleBlockerMessage(result.blockers) });
                    this.loadLifecycle(revision);
                }
            },
            error: error => {
                const detail = this.errorMessage(error, 'Could not complete lifecycle action.');
                this.lifecycleNotice = detail;
                this.messageService.add({ severity: 'error', summary: 'Lifecycle action failed', detail });
            }
        });
    }

    lifecycleActionLabel(action: string): string {
        return this.lifecycleActions().find(item => item.action === action)?.label || action;
    }

    private lifecycleBlockerMessage(blockers?: ContractValidationBlocker[]): string {
        const messages = (blockers || []).map(blocker => blocker.message).filter(Boolean);
        return messages.join(' ') || 'Review lifecycle blockers before activating.';
    }

    private errorMessage(error: any, fallback: string): string {
        const response = error?.error;
        if (typeof response === 'string' && response.trim()) {
            return response;
        }
        return response?.message || error?.message || fallback;
    }

    blockerSeverity(blocker: ContractValidationBlocker): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
        return blocker.severity === 'WARN' ? 'warn' : blocker.severity === 'INFO' ? 'info' : 'danger';
    }

    saveRenewal() {
        const revision = this.contract.revisions[this.currentRevisionNumber];
        if (!this.lifecycle?.renewal) return;
        this.contractsService.saveContractRenewal(this.contract.contractID, revision.revisionID, this.lifecycle.renewal).subscribe(renewal => {
            if (this.lifecycle) this.lifecycle.renewal = renewal;
            this.messageService.add({ severity: 'success', summary: 'Renewal saved', detail: 'Renewal decision fields updated.' });
        });
    }

    markObligationDone(obligation: ContractObligation) {
        const revision = this.contract.revisions[this.currentRevisionNumber];
        const payload = { ...obligation, status: 'COMPLETED', updatedBy: this.toolbarService.customer()?.id } as any;
        this.contractsService.saveContractObligation(this.contract.contractID, revision.revisionID, payload).subscribe(updated => {
            obligation.status = updated.status;
            obligation.completionEvidence = updated.completionEvidence;
            this.messageService.add({ severity: 'success', summary: 'Obligation updated', detail: 'Obligation marked complete.' });
            this.loadLifecycle(revision);
        });
    }
}
